/**
 * OKS-474 — §18 Crew stat-tile number formatting (crew size + total coins
 * given). Distinct from `format-compact-number.ts` (TikTok-style, abbreviates
 * at ≥1K) and `format-xp.ts`: this surface shows full locale-grouped numbers
 * below 1M and only collapses to an `M` suffix at/above 1M.
 *
 *   142_800    → "142,800" (en) / "142 800" (fr)   — full grouping, < 1M
 *   999_999    → "999,999"                          — still full
 *   1_000_000  → "1M"                               — trailing ".0" trimmed
 *   4_200_000  → "4.2M"
 *   1_284      → "1,284"   (crew size — same rule)
 *
 * # Why the `M` tier is locale-INDEPENDENT "."
 *
 * The Figma reference renders `4.2M` with a dot in every locale (matching the
 * `format-compact-number` convention used elsewhere in the app). Below 1M we
 * defer to `Intl.NumberFormat` so French sees `142 800` with its narrow-space
 * grouping; the `M` decimal is always a "." so the abbreviated form reads
 * consistently (PRD §3 / KD-4).
 *
 * Pure function; no external dependencies beyond `Intl.NumberFormat`.
 */

const safeLocale = (locale: string | undefined): string => locale ?? "fr"

/** Threshold at/above which the value collapses to an `M` suffix. */
const MILLION = 1_000_000

/**
 * @param value  A non-negative integer (crew size or coins-given total).
 * @param locale BCP-47 tag, defaults to `"fr"` (Crew default).
 * @return       Full grouped string below 1M, else a `M`-suffixed string.
 */
export function formatCrewStat(value: number, locale?: string): string {
  if (value < MILLION) {
    return new Intl.NumberFormat(safeLocale(locale)).format(value)
  }

  // Round to one decimal, then trim a trailing ".0": 1_000_000 → "1M",
  // 4_200_000 → "4.2M". Locale-independent "." (matches the Figma ref).
  const rounded = Math.round((value / MILLION) * 10) / 10
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)

  return `${text}M`
}
